import { useEffect } from 'react';
import { useAuthStore, useOnboardingStore } from '@/stores';
import { onboardingApi } from '@/services';
import { isAutomationInProgress } from '@/utils/automationStatus';

/**
 * Reconciles the client-side onboarding flags with the backend on every entry
 * into the app shell (LIL-440).
 *
 * The backend runs `OnboardingGuard` globally and default-deny: while
 * `onboarding.completed` is false, every endpoint without `@SkipOnboarding`
 * returns 403 ONBOARDING_INCOMPLETE. `completed` is only written at the very
 * end of the automation pipeline, so it stays false for the whole run — and
 * indefinitely if that run fails or was never started.
 *
 * `completed` is therefore the only signal worth gating on. An earlier version
 * keyed off `automationStatus === in_progress`, which left the failed,
 * never-started and status-lagging cases believing the app was healthy: those
 * pages fetched, got 403s, and showed the user raw errors.
 */
export function useOnboardingStatusSync() {
  useEffect(() => {
    let cancelled = false;

    async function sync() {
      try {
        const status = await onboardingApi.getOnboardingStatus();
        if (cancelled) return;

        const auth = useAuthStore.getState();

        if (status.completed) {
          if (!auth.hasCompletedOnboarding) auth.setHasCompletedOnboarding(true);
          if (auth.hasSkippedOnboarding) auth.setHasSkippedOnboarding(false);
          return;
        }

        // Not complete, so every guarded endpoint will refuse. Gate the app.
        // `hasSkippedOnboarding` has to stay true: App.tsx renders the wizard
        // unless one of the two flags is set, and flipping only `hasCompleted`
        // would bounce the user into onboarding with no explanation.
        if (auth.hasCompletedOnboarding) auth.setHasCompletedOnboarding(false);
        if (!auth.hasSkippedOnboarding) auth.setHasSkippedOnboarding(true);

        // Seed the store when a run really is under way, so the sidebar
        // indicator has something to show before the first poll lands.
        const onboarding = useOnboardingStore.getState();
        if (
          isAutomationInProgress(status.data?.automationStatus) &&
          !onboarding.automation.isRunning
        ) {
          onboarding.startAutomation();
        }
      } catch {
        // Network/BE hiccup — keep the current local flags; the automation
        // polling and socket layers have their own retry paths.
      }
    }

    void sync();
    return () => {
      cancelled = true;
    };
  }, []);
}
